import { useMemo, useState } from "react";
import {
  HStack,
  Text,
  Button,
  Stack,
  StackSeparator,
  Separator,
  Image,
  Card,
  Field,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { Comment } from "../Comment";
import { t } from "i18next";
import { NoCommentsAlert } from "../NoCommentsAlert";
import { AddGeneralComment } from "./AddGeneralComment";
import {
  CommentFilterOption,
  filterCommentByOption,
} from "../../utils/comments-utils";
import { CraComment } from "../../models/CraComment";
import { CraNode } from "../../../cra/models/CraNode";
import { useStores } from "../../../shared/stores/RootStore";
import { CustomDialog } from "../../../shared/views/CustomDialog";

type nodeId = string;
interface Props {
  onClose: () => void;
}

const CommentsDashboardModalBase: React.FC<Props> = ({ onClose }) => {
  const { mapStore, craWayStore, craNodeStore, craCommentStore } =
    useStores();
  const [filterOption, setFilterOption] = useState(
    CommentFilterOption.Unresolved
  );
  const craWay = craWayStore.getSelected()!;
  const nodes = craNodeStore.nodesByWayId.get(craWay.id)!;

  const [wayComments, nodeComments, nodesByIndex] = useMemo(() => {
    const comments = craCommentStore.commentsByCraWayId.get(craWay.id) ?? [];

    const filteredComments = comments.filter((comment) =>
      filterCommentByOption(comment, filterOption)
    );

    const wayComments: CraComment[] = [];
    const nodeComments = new Map<nodeId, CraComment[]>();

    filteredComments.forEach((comment) => {
      if (!comment.craNodeId) {
        wayComments.push(comment);
      } else {
        const prevNodeComments = nodeComments.get(comment.craNodeId);
        if (prevNodeComments) {
          prevNodeComments.push(comment);
        } else {
          nodeComments.set(comment.craNodeId, [comment]);
        }
      }
    });

    const nodeIndexes: number[] = [];

    for (const nodeId of nodeComments.keys()) {
      const nodeIndex = nodes.findIndex((node) => node.id === nodeId);

      nodeIndexes.push(nodeIndex);
    }

    nodeIndexes.sort();
    const nodesByIndex = new Map<number, CraNode>();

    nodeIndexes.forEach((nodeIndex) => {
      nodesByIndex.set(
        nodeIndex,
        craNodeStore.nodesByWayId.get(craWay.id)![nodeIndex]
      );
    });

    return [wayComments, nodeComments, nodesByIndex];
    // eslint-disable-next-line
  }, [craCommentStore.commentsByCraWayId, filterOption]);

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`map:comments.commentDashboard`)}
    >
      <Stack gap={4}>
        <Field.Root mb={4}>
          <Field.Label>{t(`map:comments.filterComments`)}</Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={filterOption}
              onChange={(e) =>
                setFilterOption(e.target.value as CommentFilterOption)
              }
            >
              <option value={CommentFilterOption.Unresolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Unresolved}`
                )}
              </option>
              <option value={CommentFilterOption.Resolved}>
                {t(
                  `map:filters.commentOptions.${CommentFilterOption.Resolved}`
                )}
              </option>
              <option value={CommentFilterOption.All}>
                {t(`map:filters.commentOptions.${CommentFilterOption.All}`)}
              </option>
            </NativeSelect.Field>
          </NativeSelect.Root>
        </Field.Root>

        <Separator />

        <Text fontSize="lg" fontWeight="medium">
          {t("map:wayComments.title")}
        </Text>
        <Stack gap={4} separator={<StackSeparator />}>
          {wayComments.map((comment, i) => (
            <Comment comment={comment} key={i} />
          ))}
          {!wayComments.length && <NoCommentsAlert />}
        </Stack>

        {filterOption !== CommentFilterOption.Resolved && (
          <AddGeneralComment craWayId={craWay.id} />
        )}

        <Separator />
        <Text fontSize="lg" fontWeight="medium">
          {t("map:nodesList.nodeComments")}
        </Text>
        <Stack gap={4} separator={<StackSeparator />}>
          {!nodesByIndex.size && <NoCommentsAlert />}
          {[...nodesByIndex.entries()].map(([nodeIndex, node], i) => (
            <HStack
              gap={4}
              key={i}
              alignItems="stretch"
              separator={<StackSeparator />}
            >
              <Stack w="30%" flexShrink={0}>
                <Text fontSize="sm" fontWeight="semibold">
                  {t("map:nodesList.titleSmall", {
                    index: nodeIndex,
                    total: nodes.length,
                  })}
                </Text>
                <Card.Root overflow="hidden">
                  <Image src={node.photo?.url} alt="" />
                </Card.Root>
                <Button
                  size="sm"
                  colorPalette="primary"
                  onClick={() => {
                    craNodeStore.select(node.id);
                    mapStore.recenterMap();
                    onClose();
                  }}
                >
                  {t("map:nodesList.viewNode")}
                </Button>
              </Stack>
              <Stack flexGrow={1} gap={4} separator={<StackSeparator />}>
                {nodeComments.get(node.id)!.map((comment, i) => (
                  <Comment comment={comment} key={i} />
                ))}
              </Stack>
            </HStack>
          ))}
        </Stack>
      </Stack>
    </CustomDialog>
  );
};

export const CommentsDashboardModal = observer(CommentsDashboardModalBase);
